import { audioRecordingModule } from "@/modules/audio-recording";
import { ASSEMBLY_AI_MULTILINGUAL_STREAMING_SPEECH_MODEL } from "@/modules/audio-recording/assembly-ai-constants";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";
import { logger } from "@/modules/logger";
import { getNetworkInfo } from "@/domains/network/status";
import { createDetachedBlobFromBufferView } from "@/modules/audio-recording/blob-utils";
import {
  MediaRecorderTranscriptFinalTurn,
  MediaRecorderTranscriptPartialTurn,
  MediaTranscriberEngine,
  MediaTranscriberEventHandlers,
  TranscriptionStatus,
} from "@/store/audio/recording/media-types";
import {
  AUDIO_RECORDER_TRANSCRIBER_SAMPLE_RATE_HZ,
  AUDIO_RECORDER_TRANSCRIBER_WS_URL,
} from "./transcriber.constants";
import audioProcessorWorklet from "@/modules/audio-recording/worklet/audio-processor.js?raw";
import {
  AssemblyAiTranscriberPayloadType,
  WebMediaTranscriberInitialConnectRetryConfig,
  WebMediaTranscriberOptions,
} from "@/store/audio/recording/media-transcriber/transcriber.types";
import { AssemblyAiToken } from "@/modules/audio-recording/audio-recording.types";
import { TranscriptionMonitor } from "@/store/audio/recording/media-transcriber/transcription-monitor";
import pRetry, { AbortError, FailedAttemptError } from "p-retry";

const DEFAULT_INITIAL_CONNECT_RETRY_CONFIG: Required<WebMediaTranscriberInitialConnectRetryConfig> =
  {
    retries: 5,
    factor: 2,
    minTimeoutMs: 500,
    maxTimeoutMs: 4_000,
  };

const WEB_MEDIA_TRANSCRIBER_TERMINATION_TIMEOUT_MS = 5_000;
const WEB_MEDIA_TRANSCRIBER_FINAL_TURN_TIMEOUT_MS = 5_000;
const WEB_MEDIA_TRANSCRIBER_WORKLET_COMMAND_TIMEOUT_MS = 1_000;

type AudioWorkletCommand = "seal" | "stop";
type AudioWorkletAcknowledgment = "sealed" | "stopped";

export class WebMediaTranscriber implements MediaTranscriberEngine {
  private readonly tokenProvider: () => Promise<AssemblyAiToken>;
  private readonly stream: MediaStream;
  private readonly sampleRateHz = AUDIO_RECORDER_TRANSCRIBER_SAMPLE_RATE_HZ;
  private readonly websocketUrl = AUDIO_RECORDER_TRANSCRIBER_WS_URL;
  private readonly requireCompleteFinalization: boolean;

  // 12 * 8ms ≈ 96ms per flush
  // This is within the bounds of what assembly-ai expects
  private readonly targetChunkCount = 12;
  private readonly maxChunkCount = 125;
  private readonly flushIntervalMs = 60;
  private readonly tailFlushTimeoutMs = 250;
  private readonly initialConnectRetryConfig: Required<WebMediaTranscriberInitialConnectRetryConfig>;

  private websocket: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private processingQueue: Promise<void> = Promise.resolve();
  private handlers: MediaTranscriberEventHandlers = {};
  private workletModuleUrl: string | null = null;
  private bufferedChunks: ArrayBuffer[] = [];
  private flushIntervalHandle: ReturnType<typeof setInterval> | null = null;
  private tailFlushTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private isPaused = false;
  private isBufferingForStart = false;
  private reconnectTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly transport = new TranscriptionMonitor({
    maxReconnectAttempts: 5,
  });
  private isReconnecting = false;
  private isStarting = false;
  private shouldAttemptReconnect = true;
  private audioCaptureReadyPromise: Promise<void> | null = null;
  private audioInputSealPromise: Promise<void> | null = null;
  private audioFinalizationPromise: Promise<void> | null = null;
  private audioDeliveryFailed = false;
  private finalTurnCount = 0;
  private finalTurnCountAtSeal: number | null = null;
  private hasUnfinalizedTurn = false;
  private hadUnfinalizedTurnAtSeal = false;
  private completeFinalTurn: ((error?: Error) => void) | null = null;
  private readonly audioWorkletAcknowledgments = new Map<
    AudioWorkletAcknowledgment,
    () => void
  >();
  private stopPromise: Promise<void> | null = null;
  private completeTermination: ((error?: Error) => void) | null = null;

  private readonly customVocabulary: string[] | undefined;

  constructor({
    tokenProvider,
    stream,
    customVocabulary,
    initialConnectRetryConfig,
    requireCompleteFinalization = false,
  }: WebMediaTranscriberOptions) {
    this.tokenProvider = tokenProvider;
    this.stream = stream;
    this.customVocabulary = customVocabulary;
    this.requireCompleteFinalization = requireCompleteFinalization;
    this.initialConnectRetryConfig = {
      ...DEFAULT_INITIAL_CONNECT_RETRY_CONFIG,
      ...initialConnectRetryConfig,
    };
  }

  setEventHandlers(handlers: MediaTranscriberEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  private emitError(
    error: unknown,
    stage?: AudioRecordingErrorStage,
    details?: Record<string, string | number | boolean | null | undefined>
  ): void {
    if (
      error instanceof AudioRecordingError &&
      error.source === AudioRecordingErrorSource.AssemblyAI
    ) {
      this.handlers.onError?.({ error });
      return;
    }
    let message = error instanceof Error ? error.message : String(error);
    if (
      (!message || message === "[object Event]") &&
      typeof Event !== "undefined" &&
      error instanceof Event
    ) {
      message = `Event: ${error.type}`;
    }
    const wrapped = new AudioRecordingError({
      source: AudioRecordingErrorSource.AssemblyAI,
      stage,
      message,
      originalError: error instanceof Error ? error : undefined,
      details,
    });
    this.handlers.onError?.({ error: wrapped });
  }

  async start(): Promise<void> {
    this.isStarting = true;
    try {
      // Capture first and hold the PCM locally while token fetch / websocket
      // setup runs. Starting the worklet after the socket connected left a
      // cold-start hole at the beginning of short recordings.
      this.isPaused = false;
      this.isBufferingForStart = true;
      this.shouldAttemptReconnect = true;
      this.clearReconnectTimer();
      this.isReconnecting = false;
      this.audioCaptureReadyPromise ??= this.ensureAudioProcessing();
      await this.audioCaptureReadyPromise;
      await this.connectForStart();
      if (!this.shouldAttemptReconnect) {
        return;
      }
      this.isBufferingForStart = false;
      await this.flushAllBufferedChunks();
      this.handlers.onStart?.();
    } catch (error) {
      if (!this.shouldAttemptReconnect) {
        return;
      }
      this.emitError(error, AudioRecordingErrorStage.Start);
      await this.stop();
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  /**
   * Resolves when microphone PCM is entering this transcriber's local buffer.
   *
   * Socket setup intentionally happens later; callers can use this boundary
   * for a recording-start cue without making the user wait on the network.
   */
  async waitForAudioCaptureReady(): Promise<void> {
    if (!this.audioCaptureReadyPromise) {
      throw new Error("Transcriber audio capture has not started.");
    }
    await this.audioCaptureReadyPromise;
  }

  /**
   * Finalizes every microphone sample preceding the sealed input boundary.
   *
   * After the worklet switches to synthetic silence, strict callers with an
   * open turn wait for AssemblyAI's `end_of_turn` event. If no turn is open,
   * the later `Termination` event authoritatively distinguishes delayed
   * transcript output from an empty capture.
   */
  async finalizePendingAudio(): Promise<void> {
    this.audioFinalizationPromise ??= this.finalizeAudioInput();
    await this.audioFinalizationPromise;
  }

  /**
   * Switches the worklet from microphone samples to synthetic silence.
   *
   * Its acknowledgment is ordered after every preceding microphone message,
   * so callers can show release feedback without waiting for network I/O.
   */
  async sealAudioInput(): Promise<void> {
    if (!this.audioInputSealPromise) {
      this.finalTurnCountAtSeal = this.finalTurnCount;
      this.hadUnfinalizedTurnAtSeal = this.hasUnfinalizedTurn;
      this.audioInputSealPromise = this.sealAndValidateAudioWorklet();
    }
    await this.audioInputSealPromise;
  }

  async stop(): Promise<void> {
    this.stopPromise ??= this.stopSession();
    await this.stopPromise;
  }

  private async stopSession(): Promise<void> {
    this.shouldAttemptReconnect = false;
    this.clearReconnectTimer();
    this.isReconnecting = false;

    let stopError: unknown;
    try {
      await this.finalizePendingAudio();
    } catch (error) {
      stopError = error;
    }

    try {
      await this.terminateSession();
    } catch (error) {
      stopError ??= error;
    } finally {
      this.teardown();
    }

    if (stopError && this.requireCompleteFinalization) {
      throw stopError;
    }
  }

  private async finalizeAudioInput(): Promise<void> {
    let finalizationError: unknown;
    try {
      await this.sealAudioInput();
      await this.flushAllBufferedChunks();
      if (this.requireCompleteFinalization) {
        await this.waitForFinalTurnAfterSeal();
      }
    } catch (error) {
      finalizationError = error;
    }

    try {
      await this.stopAndValidateAudioWorklet();
      await this.flushAllBufferedChunks();
    } catch (error) {
      finalizationError ??= error;
    }

    if (this.audioDeliveryFailed) {
      finalizationError ??= new Error(
        "Not all buffered audio reached the transcription socket."
      );
    }

    if (finalizationError) {
      throw finalizationError;
    }
  }

  /**
   * Waits for the provider's authoritative final-turn signal after sealing.
   *
   * The timeout is only a failure escape hatch. It never makes a transcript
   * eligible for posting; on expiry, the complete recorder blob wins.
   */
  private async waitForFinalTurnAfterSeal(): Promise<void> {
    const finalTurnCountAtSeal = this.finalTurnCountAtSeal;
    if (finalTurnCountAtSeal === null) {
      throw new Error("Cannot wait for a final turn before sealing audio input.");
    }
    // There is no turn to endpoint. All audio still flushes before Terminate,
    // whose acknowledgment arrives after any delayed final turns and also
    // proves the valid no-turn case.
    if (!this.hadUnfinalizedTurnAtSeal) {
      return;
    }
    if (this.finalTurnCount > finalTurnCountAtSeal) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      const complete = (error?: Error) => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (this.completeFinalTurn === complete) {
          this.completeFinalTurn = null;
        }
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };

      this.completeFinalTurn = complete;
      timeoutHandle = setTimeout(() => {
        complete(new Error("Timed out waiting for the final transcript turn."));
      }, WEB_MEDIA_TRANSCRIBER_FINAL_TURN_TIMEOUT_MS);
    });
  }

  private async sealAndValidateAudioWorklet(): Promise<void> {
    const workletSealed = await this.sendAudioWorkletCommand({
      command: "seal",
      acknowledgment: "sealed",
    });
    if (!workletSealed) {
      throw new Error("Audio worklet did not confirm its microphone boundary.");
    }
  }

  private async stopAndValidateAudioWorklet(): Promise<void> {
    const workletStopped = await this.sendAudioWorkletCommand({
      command: "stop",
      acknowledgment: "stopped",
    });
    if (!workletStopped) {
      throw new Error("Audio worklet did not confirm its finalization boundary.");
    }
  }

  /**
   * Sends a control command and waits for the matching ordered acknowledgment.
   *
   * Command timeouts report failure; they never advance transcript state.
   */
  private async sendAudioWorkletCommand({
    command,
    acknowledgment,
  }: {
    command: AudioWorkletCommand;
    acknowledgment: AudioWorkletAcknowledgment;
  }): Promise<boolean> {
    const workletNode = this.audioWorkletNode;
    if (!workletNode) {
      return false;
    }

    return new Promise<boolean>(resolve => {
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      const complete = (acknowledged: boolean) => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (this.audioWorkletAcknowledgments.get(acknowledgment) === completeFromWorklet) {
          this.audioWorkletAcknowledgments.delete(acknowledgment);
        }
        resolve(acknowledged);
      };

      const completeFromWorklet = () => complete(true);
      this.audioWorkletAcknowledgments.set(acknowledgment, completeFromWorklet);
      timeoutHandle = setTimeout(() => {
        const error = new Error(`Timed out waiting for audio worklet to ${command}.`);
        logger.warn({
          message: `[WebMediaTranscriber] timed out waiting for audio worklet to ${command}`,
        });
        this.emitError(error, AudioRecordingErrorStage.Stop);
        complete(false);
      }, WEB_MEDIA_TRANSCRIBER_WORKLET_COMMAND_TIMEOUT_MS);

      try {
        workletNode.port.postMessage({ type: command });
      } catch (error) {
        this.emitError(error, AudioRecordingErrorStage.Stop);
        complete(false);
      }
    });
  }

  private async flushAllBufferedChunks(): Promise<void> {
    while (this.bufferedChunks.length > 0) {
      await this.flushBufferedChunks(true);
    }
    await this.processingQueue;
  }

  /**
   * Gracefully ends the v3 streaming session.
   *
   * AssemblyAI sends any remaining Turn events before Termination. Waiting
   * for that acknowledgment keeps stop() from tearing down the socket while
   * the final words are still being transcribed.
   */
  private async terminateSession(): Promise<void> {
    const websocket = this.websocket;
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      if (this.requireCompleteFinalization) {
        throw new Error(
          "Cannot prove session termination without an open transcription socket."
        );
      }
      return;
    }

    await new Promise<void>((resolve, reject) => {
      let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
      const complete = (error?: Error) => {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
          timeoutHandle = null;
        }
        if (this.completeTermination === complete) {
          this.completeTermination = null;
        }
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };

      this.completeTermination = complete;
      timeoutHandle = setTimeout(() => {
        const error = new Error("Timed out waiting for session termination.");
        logger.warn({
          message: "[WebMediaTranscriber] timed out waiting for session termination",
        });
        complete(error);
      }, WEB_MEDIA_TRANSCRIBER_TERMINATION_TIMEOUT_MS);

      try {
        websocket.send(JSON.stringify({ type: "Terminate" }));
      } catch (error) {
        this.emitError(error, AudioRecordingErrorStage.Stop);
        complete(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  dispose(): void {
    void this.stop().catch(() => undefined);
  }

  private async openWebSocket(token: AssemblyAiToken): Promise<void> {
    const wsUrl = new URL(this.websocketUrl);
    const params: Record<string, string> = {
      sample_rate: String(this.sampleRateHz),
      token: token.token,
      format_turns: "true",
      speech_model: ASSEMBLY_AI_MULTILINGUAL_STREAMING_SPEECH_MODEL,
      language_detection: "true",
    };

    if (this.customVocabulary && this.customVocabulary.length > 0) {
      // AssemblyAI expects a stringified array of strings for 'keyterms_prompt'
      // https://www.assemblyai.com/docs/api-reference/streaming/realtime#query-parameters
      params.keyterms_prompt = JSON.stringify(this.customVocabulary);
    }

    Object.entries(params).forEach(([key, value]) => {
      wsUrl.searchParams.set(key, value);
    });

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(wsUrl.toString());
      ws.binaryType = "arraybuffer";
      this.websocket = ws;

      const handleOpen = () => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("error", handleConnectionError);
        ws.removeEventListener("close", handleConnectionClose);
        ws.addEventListener("message", this.handleMessage);
        ws.addEventListener("close", this.handleClose);
        ws.addEventListener("error", this.handleSocketError);
        resolve();
      };

      const handleConnectionError = (event: Event) => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("error", handleConnectionError);
        ws.removeEventListener("close", handleConnectionClose);
        const networkInfo = getNetworkInfo();
        reject(
          new AudioRecordingError({
            source: AudioRecordingErrorSource.AssemblyAI,
            stage: AudioRecordingErrorStage.WebsocketConnect,
            message: "WebSocket connection failed.",
            originalError: event instanceof Error ? event : undefined,
            details: {
              readyState: ws.readyState,
              online: networkInfo.online ?? undefined,
              effectiveType: networkInfo.effectiveType,
            },
          })
        );
      };

      const handleConnectionClose = (event: CloseEvent) => {
        ws.removeEventListener("open", handleOpen);
        ws.removeEventListener("error", handleConnectionError);
        ws.removeEventListener("close", handleConnectionClose);
        reject(
          new AudioRecordingError({
            source: AudioRecordingErrorSource.AssemblyAI,
            stage: AudioRecordingErrorStage.WebsocketConnect,
            message: "WebSocket closed before connecting.",
            details: {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
            },
          })
        );
      };

      ws.addEventListener("open", handleOpen);
      ws.addEventListener("error", handleConnectionError);
      ws.addEventListener("close", handleConnectionClose);
    });
  }

  private handleMessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") return;

    let rawPayload: unknown;
    try {
      rawPayload = JSON.parse(event.data);
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.PayloadParse);
      return;
    }

    const payloadType = audioRecordingModule.resolveAssemblyAiRealtimePayloadType({
      raw: rawPayload,
    });

    this.emitStatusChange(this.transport.markConnected());

    switch (payloadType) {
      /**
       * If the payload is a turn, map it to a transcript turn and emit it.
       */
      case AssemblyAiTranscriberPayloadType.Turn: {
        const turn = audioRecordingModule.mapAssemblyAiTurnPayloadToRecorderTurn({
          raw: rawPayload,
        });
        const isEndOfTurn =
          typeof rawPayload === "object" &&
          rawPayload !== null &&
          ((rawPayload as { end_of_turn?: unknown }).end_of_turn === true ||
            (rawPayload as { endOfTurn?: unknown }).endOfTurn === true);
        if (!turn) {
          if (isEndOfTurn) this.recordFinalTurn();
          return;
        }

        if (turn.isEndOfTurn !== true) {
          this.hasUnfinalizedTurn = true;
          this.handlers.onTranscriptPartial?.({
            partialTurn: turn as MediaRecorderTranscriptPartialTurn,
          });
          return;
        }

        this.recordFinalTurn();
        this.handlers.onTranscriptFinal?.({
          finalTurn: turn as MediaRecorderTranscriptFinalTurn,
        });
        return;
      }

      case AssemblyAiTranscriberPayloadType.Termination: {
        this.completeTermination?.();
        return;
      }

      /**
       * Else, no-op. We just ignore the other payload types.
       */
      default: {
        return;
      }
    }
  };

  async pause(): Promise<void> {
    if (this.isPaused) return;
    this.isPaused = true;

    try {
      await this.audioCaptureReadyPromise;
      if (this.audioContext && this.audioContext.state !== "closed") {
        await this.audioContext.suspend?.();
      }

      // Startup owns the cold buffer until a socket exists. Once connected,
      // start() flushes the pre-pause audio while preserving this pause.
      if (this.isBufferingForStart) {
        this.clearFlushTimers();
        return;
      }

      while (this.bufferedChunks.length > 0) {
        await this.flushBufferedChunks(true);
      }

      await this.processingQueue.catch(() => undefined);
      this.clearFlushTimers();
    } catch (error) {
      this.isPaused = false;
      this.emitError(error, AudioRecordingErrorStage.Pause);
      throw error;
    }
  }

  async resume(): Promise<void> {
    if (!this.isPaused) return;

    try {
      if (!this.audioContext) {
        await this.ensureAudioProcessing();
      } else if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      this.isPaused = false;
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.Resume);
      throw error;
    }
  }

  private handleClose = (event: CloseEvent) => {
    this.completeFinalTurn?.(
      new Error(`WebSocket closed before the final transcript turn (${event.code}: ${event.reason}).`)
    );
    this.completeTermination?.(
      new Error(`WebSocket closed before session termination (${event.code}: ${event.reason}).`)
    );
    logger.warn({
      message: "[WebMediaTranscriber] WebSocket closed",
      info: {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      },
    });
    this.emitStatusChange(this.transport.markDisconnected());
    this.scheduleReconnect();
  };

  private handleSocketError = (event: Event) => {
    this.completeFinalTurn?.(new Error("WebSocket failed before the final transcript turn."));
    this.completeTermination?.(new Error("WebSocket failed before session termination."));
    const networkInfo = getNetworkInfo();
    this.emitError(event, AudioRecordingErrorStage.WebsocketError, {
      readyState: this.websocket?.readyState ?? undefined,
      online: networkInfo.online ?? undefined,
      effectiveType: networkInfo.effectiveType,
    });
    this.emitStatusChange(this.transport.markDisconnected());
    this.scheduleReconnect();
  };

  private async tryReconnect(): Promise<void> {
    this.clearReconnectTimer();
    let shouldScheduleRetry = false;
    this.isReconnecting = true;
    try {
      // If socket is still open, no need to reconnect.
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        return;
      }

      this.cleanupWebSocket();
      const token = await this.tokenProvider();
      if (!this.shouldAttemptReconnect) {
        return;
      }
      await this.openWebSocket(token);
      if (!this.shouldAttemptReconnect) {
        this.cleanupWebSocket();
        return;
      }
      await this.ensureAudioProcessing();
      this.emitStatusChange(this.transport.markConnected());
    } catch (error) {
      if (!this.shouldAttemptReconnect) {
        return;
      }
      this.emitError(error, AudioRecordingErrorStage.Reconnect);
      const statusChange = this.transport.recordReconnectFailure();
      this.emitStatusChange(statusChange);
      if (this.transport.status !== TranscriptionStatus.Compromised) {
        shouldScheduleRetry = true;
      }
    } finally {
      this.isReconnecting = false;
      if (shouldScheduleRetry) {
        this.scheduleReconnect();
      }
    }
  }

  private emitStatusChange(status: TranscriptionStatus | null) {
    if (!status) return;
    this.handlers.onStatusChange?.({ status });
  }

  private scheduleReconnect() {
    if (this.isReconnecting) return;
    if (this.isStarting) return;
    if (this.transport.status === TranscriptionStatus.Compromised) return;
    if (!this.shouldAttemptReconnect) return;
    if (this.reconnectTimeoutHandle) return;

    this.isReconnecting = true;
    this.reconnectTimeoutHandle = setTimeout(() => {
      this.reconnectTimeoutHandle = null;
      void this.tryReconnect();
    }, 500);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimeoutHandle) {
      clearTimeout(this.reconnectTimeoutHandle);
      this.reconnectTimeoutHandle = null;
    }
  }

  private async connectForStart(): Promise<void> {
    await pRetry(
      async () => {
        if (!this.shouldAttemptReconnect) {
          throw new AbortError("Transcriber stopped before initial setup completed.");
        }

        const token = await this.tokenProvider();
        if (!this.shouldAttemptReconnect) {
          throw new AbortError("Transcriber stopped before websocket setup completed.");
        }
        await this.openWebSocket(token);
        if (!this.shouldAttemptReconnect) {
          this.cleanupWebSocket();
          throw new AbortError("Transcriber stopped while websocket setup was completing.");
        }
        this.emitStatusChange(this.transport.markConnected());
      },
      {
        retries: this.initialConnectRetryConfig.retries,
        factor: this.initialConnectRetryConfig.factor,
        minTimeout: this.initialConnectRetryConfig.minTimeoutMs,
        maxTimeout: this.initialConnectRetryConfig.maxTimeoutMs,
        onFailedAttempt: (_error: FailedAttemptError) => {
          this.cleanupWebSocket();
          this.emitStatusChange(this.transport.markDisconnected());
        },
      }
    );
  }

  private teardown() {
    this.shouldAttemptReconnect = false;
    this.isBufferingForStart = false;
    for (const complete of this.audioWorkletAcknowledgments.values()) {
      complete();
    }
    this.audioWorkletAcknowledgments.clear();
    this.clearReconnectTimer();
    this.cleanupWebSocket();
    this.disposeAudioContext();
    this.clearBufferedChunks();
    this.handlers.onStop?.();
  }

  private cleanupWebSocket() {
    if (!this.websocket) return;
    this.websocket.removeEventListener("message", this.handleMessage);
    this.websocket.removeEventListener("close", this.handleClose);
    this.websocket.removeEventListener("error", this.handleSocketError);

    try {
      this.websocket.close();
    } catch (_) {
      /* noop */
    }

    this.websocket = null;
  }

  private disposeAudioContext() {
    if (this.audioWorkletNode) {
      this.audioWorkletNode.port.onmessage = null;

      try {
        this.audioWorkletNode.disconnect();
      } catch {
        /* noop */
      }

      this.audioWorkletNode = null;
    }

    if (this.audioSource) {
      try {
        this.audioSource.disconnect();
      } catch {
        /* noop */
      }

      this.audioSource = null;
    }

    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }

    if (this.workletModuleUrl) {
      URL.revokeObjectURL(this.workletModuleUrl);
      this.workletModuleUrl = null;
    }

    this.clearFlushTimers();
  }

  private async streamSamples(samples: Int16Array): Promise<void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      this.audioDeliveryFailed = true;
      this.emitStatusChange(this.transport.markDisconnected());
      this.scheduleReconnect();
      return;
    }

    const blob = createDetachedBlobFromBufferView(samples);
    try {
      this.websocket.send(blob);
    } catch (error) {
      const networkInfo = getNetworkInfo();
      this.emitError(error, AudioRecordingErrorStage.WebsocketSend, {
        readyState: this.websocket?.readyState ?? undefined,
        online: networkInfo.online ?? undefined,
        effectiveType: networkInfo.effectiveType,
      });
      this.audioDeliveryFailed = true;
      this.emitStatusChange(this.transport.markDisconnected());
      this.scheduleReconnect();
    }
  }

  private async ensureAudioProcessing(): Promise<void> {
    if (this.audioContext) {
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume().catch(error => {
          this.emitError(error, AudioRecordingErrorStage.AudioContextResume);
        });
      }
      return;
    }

    const context = new AudioContext({
      sampleRate: this.sampleRateHz,
      latencyHint: "interactive",
    });

    try {
      const processorBlob = new Blob([audioProcessorWorklet], { type: "application/javascript" });
      const moduleUrl = URL.createObjectURL(processorBlob);
      this.workletModuleUrl = moduleUrl;
      await context.audioWorklet.addModule(moduleUrl);

      if (!this.shouldAttemptReconnect) {
        await context.close().catch(() => undefined);
        if (this.workletModuleUrl === moduleUrl) {
          URL.revokeObjectURL(moduleUrl);
          this.workletModuleUrl = null;
        }
        return;
      }

      const source = context.createMediaStreamSource(this.stream);
      const workletNode = new AudioWorkletNode(context, "audio-processor");
      source.connect(workletNode);
      workletNode.connect(context.destination);

      workletNode.port.onmessage = this.handleAudioWorkletMessage;

      this.audioContext = context;
      this.audioSource = source;
      this.audioWorkletNode = workletNode;

      if (this.isPaused && context.state !== "suspended") {
        await context.suspend?.();
      } else if (context.state === "suspended") {
        await context.resume().catch(error => {
          this.emitError(error, AudioRecordingErrorStage.AudioContextResume);
        });
      }
    } catch (error) {
      this.emitError(error, AudioRecordingErrorStage.AudioWorklet);
      await context.close().catch(() => undefined);
      this.audioContext = null;
      this.audioSource = null;
      this.audioWorkletNode = null;
      if (this.workletModuleUrl) {
        URL.revokeObjectURL(this.workletModuleUrl);
        this.workletModuleUrl = null;
      }
      throw error;
    }
  }

  private enqueueChunk(buffer: ArrayBuffer) {
    if (this.isPaused || this.isBufferingForStart) {
      this.bufferedChunks.push(buffer);
      return;
    }

    this.bufferedChunks.push(buffer);
    this.scheduleFlushIfNeeded();
  }

  private handleAudioWorkletMessage = (event: MessageEvent) => {
    const payload = event.data as {
      audio_data?: ArrayBuffer;
      type?: string;
    };

    if (payload.type === "sealed" || payload.type === "stopped") {
      this.audioWorkletAcknowledgments.get(payload.type)?.();
      return;
    }

    if (payload.audio_data) {
      this.enqueueChunk(payload.audio_data);
    }
  };

  private recordFinalTurn(): void {
    this.finalTurnCount += 1;
    this.hasUnfinalizedTurn = false;
    this.completeFinalTurn?.();
  }

  private scheduleFlushIfNeeded() {
    if (this.isPaused || this.isBufferingForStart) {
      return;
    }

    if (this.bufferedChunks.length >= this.targetChunkCount) {
      void this.flushBufferedChunks();
    }

    if (!this.flushIntervalHandle) {
      this.flushIntervalHandle = setInterval(() => {
        void this.flushBufferedChunks();
      }, this.flushIntervalMs);
    }

    this.resetTailFlushTimeout();
  }

  private resetTailFlushTimeout() {
    if (this.tailFlushTimeoutHandle) {
      clearTimeout(this.tailFlushTimeoutHandle);
      this.tailFlushTimeoutHandle = null;
    }

    this.tailFlushTimeoutHandle = setTimeout(() => {
      void this.flushBufferedChunks(true);
    }, this.tailFlushTimeoutMs);
  }

  private async flushBufferedChunks(force = false): Promise<void> {
    if (this.bufferedChunks.length === 0) return;
    const available = this.bufferedChunks.length;
    if (!force && available < this.targetChunkCount) {
      return;
    }

    const chunkCount = Math.min(force ? available : this.targetChunkCount, this.maxChunkCount);
    const chunks = this.bufferedChunks.splice(0, chunkCount);
    const combined = this.combineChunks(chunks);

    const samples = new Int16Array(combined);
    this.processingQueue = this.processingQueue
      .then(() => this.streamSamples(samples))
      .catch(error => {
        this.emitError(error, AudioRecordingErrorStage.AudioProcessing);
      });

    if (this.bufferedChunks.length === 0 && force) {
      this.clearFlushTimers();
    }
  }

  private combineChunks(chunks: ArrayBuffer[]): ArrayBuffer {
    let totalBytes = 0;
    for (const chunk of chunks) {
      totalBytes += chunk.byteLength;
    }

    const combined = new Uint8Array(totalBytes);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(new Uint8Array(chunk), offset);
      offset += chunk.byteLength;
    }

    return combined.buffer;
  }

  private clearFlushTimers() {
    if (this.flushIntervalHandle) {
      clearInterval(this.flushIntervalHandle);
      this.flushIntervalHandle = null;
    }
    if (this.tailFlushTimeoutHandle) {
      clearTimeout(this.tailFlushTimeoutHandle);
      this.tailFlushTimeoutHandle = null;
    }
  }

  private clearBufferedChunks() {
    this.bufferedChunks = [];
    this.clearFlushTimers();
  }
}
